import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AnimatedSection, NoiseTexture } from "../shared";
import { UTILITY_ITEMS } from "./constants";
import currentGradient from "../../../../assets/corner-gradient.webp";

export default function TokenUtility() {
  const [activeIdx, setActiveIdx] = useState(4);
  const activeItem = UTILITY_ITEMS[activeIdx];

  return (
    <section
      id="token"
      className="py-16 md:py-24 px-6 relative overflow-hidden bg-[#0a0a0a]"
    >
      <NoiseTexture />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_30%_50%,_rgba(120,80,255,0.08),_transparent)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_50%_50%_at_70%_50%,_rgba(255,100,180,0.06),_transparent)]" />

      <div className="max-w-7xl max-lg:max-w-[600px] mx-auto w-full relative z-10">
        <AnimatedSection>
          <div className="mb-12">
            <span className="inline-flex items-center gap-2.5 text-[11px] font-semibold tracking-[0.2em] uppercase text-white/35 mb-5 before:content-[''] before:w-5 before:h-px before:bg-gradient-to-r before:from-violet-500/60 before:to-transparent">
              Why AGNT Hub
            </span>
            <h2 className="font-space-grotesk text-4xl md:text-[60px] font-bold tracking-[-0.03em] gradient-text-bright leading-tight">
              $ATTS Token
            </h2>
          </div>
        </AnimatedSection>

        <div className="grid lg:grid-cols-2 gap-0 relative">
          {/* Left column — description card */}
          <div className="bg-[#111] max-lg:h-[506px] overflow-hidden lg:h-[560px] rounded-[16px] border border-white/[0.06] p-6 lg:p-10 flex flex-col gap-3  lg:gap-6 relative lg:flex-1 lg:min-w-0">
            <img
              src={currentGradient}
              alt="Corner Gradient"
              className="absolute -top-2 left-[-2px] w-full h-full object-cover"
            />

            <div className="absolute inset-0 opacity-[0.04] pointer-events-none">
              <NoiseTexture />
            </div>
            <div className="relative z-10 flex flex-col gap-6 max-w-[435px]">
              <p
                className="font-space-grotesk text-[22px] md:text-[30px] font-bold leading-[1.375]"
                style={{
                  background:
                    "linear-gradient(114.14deg, #fff 30%, #6b7280 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {`The $ATTS token is the core utility asset powering the AGNT.Hub ecosystem, designed to align incentives and fuel activity across X Mini Apps. Its key utilities include: `}
              </p>

              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIdx}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white/[0.03] border border-white/[0.06] rounded-[12px] max-lg:p-3 p-[25px]"
                >
                  <div className="flex flex-col gap-2">
                    <h3 className="font-space-grotesk text-[18px] font-bold text-white/80 leading-[28px]">
                      {activeItem.title}
                    </h3>
                    <p className="text-[16px] text-white/55 leading-[26px]">
                      {activeItem.desc}
                    </p>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
          {/* LOGO */}
          <div className="absolute max-xl:hidden overflow-hidden z-[2] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 size-[238px] bg-gradient-to-b from-white/10 to-zinc-800/10 bg-clip-padding rounded-full shadow-[0px_15px_50px_0px_rgba(0,0,0,0.05)] ring-inset ring-2 ring-white/15 backdrop-blur-3xl">
            <img
              src={"/logo-agnt.svg"}
              width={159}
              height={149}
              alt="ATTS Token"
              className="  z-10 size-[340px] max-w-none max-h-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            />
          </div>
          {/* Right column — illustration + utility cards */}
          <div className="relative lg:w-full xl:w-[640px] flex-shrink-0 flex flex-col max-xl:justify-center lg:flex-row items-center h-full">
            <span className="size-3 hidden xl:block bg-white left-0 rounded-full translate-x-[calc(50%+107px)] top-1/2 -translate-y-[50.6%] z-[1]  absolute" />

            {/* Utility cards */}
            <div className="flex flex-col gap-3 lg:gap-[50px] w-full lg:w-[413px]  xl:ml-auto mt-6 lg:mt-0 lg:py-4 justify-center">
              {UTILITY_ITEMS.map(
                ({ lineIcon: Icon, icon, title, id, linePosition }, i) => {
                  const isActive = i === activeIdx;
                  return (
                    <motion.button
                      key={id}
                      onClick={() => setActiveIdx(i)}
                      className={`flex relative items-center box-border  gap-4 pl-[6px] pr-[6px] lg:pr-[22px] py-[6px] rounded-[16px] text-left transition-colors duration-300 cursor-pointer border lg:h-[56px] h-[48px] ${
                        isActive
                          ? "border-white/[0.12] bg-[#161616]"
                          : "border-white/[0.06] bg-[#111] hover:border-white/[0.1] hover:bg-[#151515]"
                      }`}
                    >
                      <Icon
                        className={`absolute left-0 max-xl:hidden -translate-x-full ${linePosition} ${isActive ? "text-white" : "text-[#FFFFFF0F]"}`}
                      />
                      <div className="size-9 lg:size-11 flex items-center justify-center bg-white/[0.03] border border-white/[0.06] rounded-[10px] flex-shrink-0 overflow-hidden">
                        <img
                          src={icon}
                          alt=""
                          className="w-5 lg:w-6 h-5 lg:h-6"
                        />
                      </div>
                      <span className="font-space-grotesk text-[16px] lg:text-[22px] font-bold text-white leading-[32px]">
                        {title}
                      </span>
                    </motion.button>
                  );
                },
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
